import React from 'react';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import '../../shared/theme/theme.scss';
import './talentPartnerCreatorGate.scss';
import { useInfluencerUser } from '../hooks/useInfluencerUser';

const SWITCH_CREATOR = '/switch/user';

/**
 * Keeps a Talent Partner's creator pages (Zap, Storefront, Link in Bio, Social
 * Connect, Subscription) on one creator at a time.
 *
 * These pages act for UserData.influencer_id: after a pick on /switch/user that
 * is the creator (flowUser.ts), before a pick it is the Talent Partner's own
 * row — whose Instagram / YouTube / Facebook, automations and plan would be
 * nobody's. So:
 * - a Talent Partner with no creator picked gets "Choose a creator first";
 * - with one picked, the page says which creator it is for, with Switch creator
 *   (`showCreator`; off where the page already names the creator);
 * - everyone else gets the page as it was.
 */
export function TalentPartnerCreatorGate({ children, showCreator = true }: { children: React.ReactNode; showCreator?: boolean }) {
  const { influencerId, isTalentPartner, user } = useInfluencerUser();
  if (!isTalentPartner) return <>{children}</>;

  if (!influencerId) {
    return (
      <div className="brd brd-page">
        <div className="brd-empty">
          <div className="brd-empty__icon"><UserRound size={30} /></div>
          <p className="brd-empty__title">Choose a creator first</p>
          <p className="brd-empty__body">
            Zap, connected social accounts and plans belong to a creator. Pick the creator you are working on, then come back.
          </p>
          <Link className="brd-btn brd-btn--solid" to={SWITCH_CREATOR}>Choose a creator</Link>
        </div>
      </div>
    );
  }

  const name = String(user?.name || '').trim() || 'this creator';
  return (
    <>
      {showCreator && (
        <div className="brd inf-tp-creator">
          <div className="brd-note inf-tp-creator__note">
            <UserRound size={15} aria-hidden />
            <span className="brd-note__text">
              Working on <strong>{name}</strong>. Everything on this page is for this creator.
            </span>
            <Link className="brd-note__link" to={SWITCH_CREATOR}>Switch creator</Link>
          </div>
        </div>
      )}
      {children}
    </>
  );
}

export default TalentPartnerCreatorGate;
